import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';

export interface UserPreferencesDto {
  language: string | null;
  timezone: string | null;
  dateFormat: string | null;
  timeFormat: string | null;
}

export interface ProfileResponse {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl?: string | null;
  color?: string;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  preferences: UserPreferencesDto | null;
}

interface UpdateProfileBody {
  displayName: string | null;
  color: string | null;
  avatarUrl: string | null;
}

interface UpdatePreferencesBody {
  language?: string | null;
  timezone?: string | null;
  dateFormat?: string | null;
  timeFormat?: string | null;
}

export const profileService = {
  /**
   * Obtiene el perfil del usuario autenticado.
   * No requiere headers adicionales — httpClient inyecta el token automáticamente.
   */
  getProfile: (signal?: AbortSignal) =>
    signal
      ? httpClient.get<ProfileResponse>(`${API_V1}/identity/me`, { signal })
      : httpClient.get<ProfileResponse>(`${API_V1}/identity/me`),

  /**
   * Actualiza displayName, color y/o avatarUrl del perfil.
   * Solo envía los campos definidos (patch parcial).
   */
  updateProfile: (data: { displayName: string; color: string; avatarUrl?: string | null }) =>
    httpClient.patch<UpdateProfileBody, void>(`${API_V1}/identity/me`, {
      displayName: data.displayName,
      color: data.color,
      avatarUrl: data.avatarUrl ?? null,
    }),

  /**
   * Actualiza las preferencias del usuario autenticado.
   * Solo envía los campos definidos (patch parcial) — un campo omitido no
   * modifica su valor en el servidor; enviarlo como `null` sí lo limpia.
   */
  updatePreferences: (data: UpdatePreferencesBody) =>
    httpClient.patch<UpdatePreferencesBody, void>(`${API_V1}/identity/me/preferences`, data),

  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.patch<FormData, void>(`${API_V1}/identity/profile/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAvatar: () => httpClient.delete<void>(`${API_V1}/identity/profile/avatar`),
};
